import { LoadEntityAttributes } from "./entityAttribute.types";
import { buildEntityFieldCatalog } from "./entityFieldCatalog";
import { EntityFieldCatalogEntry } from "./entityFieldCatalog.types";

// Entity metadata doesn't change while someone is working, so it is read once per
// entity and held for the session, with concurrent consumers sharing the request.
const catalogsByEntityName = new Map<string, EntityFieldCatalogEntry[]>();
const requestsByEntityName = new Map<
  string,
  Promise<EntityFieldCatalogEntry[]>
>();

/** Only for tests - the cache is otherwise session-lifetime by design. */
export function clearEntityCatalogCache() {
  catalogsByEntityName.clear();
  requestsByEntityName.clear();
}

/** What has been loaded for an entity so far, or undefined before it has. */
export function getCachedCatalog(entityName: string) {
  return catalogsByEntityName.get(entityName);
}

/** Holds a catalog the metadata couldn't supply, so a failed read isn't retried per render. */
export function setCachedCatalog(
  entityName: string,
  entries: EntityFieldCatalogEntry[]
) {
  catalogsByEntityName.set(entityName, entries);
}

export function loadCatalog(entityName: string, load: LoadEntityAttributes) {
  const cached = catalogsByEntityName.get(entityName);
  if (cached) {
    return Promise.resolve(cached);
  }

  const inFlight = requestsByEntityName.get(entityName);
  if (inFlight) {
    return inFlight;
  }

  const request = load(entityName)
    .then((attributes) => {
      const entries = buildEntityFieldCatalog(attributes);
      catalogsByEntityName.set(entityName, entries);

      return entries;
    })
    .finally(() => {
      requestsByEntityName.delete(entityName);
    });

  requestsByEntityName.set(entityName, request);

  return request;
}
